/* Space Armory cart operations. A successful mutation is never retried to repair a view. */
(function (scope) {
  'use strict';
  // French, Spanish and German pages: messages from window.SA_I18N (snippets/sa-i18n-js.liquid); English pages keep these.
  const coreText = (key, fallback) => scope.SA_I18N?.[key] ?? fallback;
  const SWAP = '_SpaceArmoryVariantSwap';
  const COVER = '_SpaceArmoryCheckoutPlus';
  const isCoverage = (item, config) => String(item.product_id) === String(config.protection?.id);
  const merchandise = (cart, config) => cart.items.filter(item => !isCoverage(item, config));
  function protectionVariant(cart, config) {
    const items = merchandise(cart, config);
    if (!items.some(item => item.requires_shipping !== false) || !config.protection?.variants?.length) return null;
    const rate = Number(config.rate);
    if (!(rate > 0)) return null;
    const subtotal = items.reduce((sum, item) => sum + item.final_line_price, 0) / rate;
    const index = subtotal < 9999 ? 0 : subtotal < 15000 ? 1 : 2;
    const variant = config.protection.variants[index];
    return variant?.available ? variant : null;
  }
  function checkoutReady(cart, config) {
    if (!cart || !merchandise(cart, config).length || cart.items.some(item => item.properties?.[SWAP])) return false;
    const coverage = cart.items.filter(item => isCoverage(item, config));
    if (!coverage.length) return true;
    const target = protectionVariant(cart, config);
    return coverage.length === 1 && coverage[0].quantity === 1 && String(coverage[0].variant_id) === String(target?.id);
  }
  // Shopify gives the other lines new keys after a cart write (seen live 2026-10-18: a size change added the new size,
  // then the old key answered 400 and both sizes stayed in the cart). A swap therefore also remembers the original
  // line by its variant and properties, which stay the same, and finds it again in the latest cart.
  const lineSignature = item => {
    const properties = Object.entries(item.properties || {}).filter(([name]) => name !== SWAP).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    let hash = 5381;
    for (const character of JSON.stringify([String(item.variant_id), properties])) hash = ((hash * 33) ^ character.charCodeAt(0)) >>> 0;
    return hash.toString(36);
  };
  const readJournal = item => {
    if (!item.properties?.[SWAP]) return null;
    try {
      const journal = JSON.parse(item.properties[SWAP]);
      return journal?.originalKey && (journal.version === 1 || journal.version === 2) ? journal : null;
    } catch { return null; }
  };
  class CartStore {
    constructor({request, config, onChange = () => {}, onBusy = () => {}, onError = () => {}}) {
      Object.assign(this, {request, config, onChange, onBusy, onError});
      this.cart = null;
      this.tail = Promise.resolve();
      this.pending = 0;
      this.mutating = 0;
      this.confirmedAt = 0;
      this.needsRead = false;
    }
    async read() {
      const cart = await this.request('cart.js');
      if (!cart || !Array.isArray(cart.items) || !cart.currency) throw new Error(coreText('cart.error_load', 'Your cart could not be loaded. Please try again.'));
      this.cart = cart;
      this.confirmedAt = Date.now();
      this.needsRead = false;
      this.unconfirmed = false;
      return cart;
    }
    async post(route, body) { this.needsRead = true; if (route === 'cart/add.js') this.added = true; return this.request(route, body); }
    async change(key, quantity, properties) {
      const payload = {id: key, quantity};
      if (properties !== undefined) payload.properties = properties;
      this.cart = await this.post('cart/change.js', payload);
      this.confirmedAt = Date.now();
      this.needsRead = false;
      return this.cart;
    }
    swapOriginal(journal) {
      return this.cart.items.find(item => item.key === journal.originalKey && !item.properties?.[SWAP]) ||
        (journal.original ? this.cart.items.find(item => !item.properties?.[SWAP] && String(item.variant_id) === String(journal.variant) && lineSignature(item) === journal.original) : undefined);
    }
    async recoverSwaps() {
      // One unfinished swap at a time, read from the latest cart, since each write can re-key the other lines.
      for (let guard = 0; guard < 20; guard++) {
        const entry = this.cart.items.find(item => readJournal(item));
        if (!entry) return;
        const journal = readJournal(entry);
        const original = this.swapOriginal(journal);
        if (original) await this.change(entry.key, 0);
        else {
          const properties = {...entry.properties};
          delete properties[SWAP];
          await this.change(entry.key, entry.quantity, properties);
        }
      }
    }
    async reconcileCoverage(choice) {
      const existing = this.cart.items.filter(item => isCoverage(item, this.config));
      const selected = choice === undefined ? existing.length > 0 : choice;
      const target = selected ? protectionVariant(this.cart, this.config) : null;
      let kept = false;
      for (const item of existing) {
        if (target && String(item.variant_id) === String(target.id) && !kept) {
          kept = true;
          if (item.quantity !== 1) await this.change(item.key, 1);
        } else await this.change(item.key, 0);
      }
      if (target && !kept) {
        await this.post('cart/add.js', {items: [{id: target.id, quantity: 1, properties: {[COVER]: 'Order protection'}}]});
        await this.read();
      }
      if (choice === true && !target) throw new Error(coreText('cart.error_protection', 'Checkout+ is unavailable for this cart. You can continue without it.'));
    }
    run(operation, {repair = true, forceRead = false, readonly = false, protection = false} = {}) {
      this.pending += 1;
      if (!readonly) this.mutating += 1;
      this.onBusy(true);
      const work = this.tail.catch(() => {}).then(async () => {
        this.added = false;
        try {
          if (forceRead || !this.cart || this.unconfirmed || Date.now() - this.confirmedAt > 15000) await this.read();
          await this.recoverSwaps();
          const result = await operation();
          // The requested write has succeeded. A later refresh failure must not make it look safe to repeat it.
          try {
            if (this.needsRead) await this.read();
            // protection: Undo of the last item asks for Checkout+ again, when this cart can have it.
            if (repair) await this.reconcileCoverage(protection && protectionVariant(this.cart, this.config) ? true : undefined);
          } catch (error) {
            this.onError(coreText('cart.error_refresh', 'Your change was saved, but the cart could not be refreshed. Reopen the cart before continuing.'));
            this.unconfirmed = true;
          }
          // A browser that blocks all cookies starts a new cart on every request (seen live 2026-10-27, iPhone Safari):
          // the add answers 200, yet the cart reads back empty.
          if (this.added && !this.unconfirmed && !this.cart.items.length) this.onError(coreText('cart.error_cookies', 'Your browser is blocking cookies, so the cart can\u2019t save items. To order on iPhone: Settings \u2192 Apps \u2192 Safari \u2192 turn off \u201cBlock All Cookies\u201d, then reload this page.'));
          this.onChange(this.cart);
          return result;
        } catch (error) {
          try {
            await this.read();
            await this.recoverSwaps();
            this.onChange(this.cart);
          } catch { this.unconfirmed = true; }
          this.onError(error.message || coreText('cart.error_unconfirmed', 'We could not confirm this change. Please review your cart before trying again.'));
          throw error;
        }
      }).finally(() => {
        this.pending -= 1;
        if (!readonly) this.mutating -= 1;
        this.onBusy(this.pending > 0);
      });
      this.tail = work;
      return work;
    }
    refresh({maxAge = 0} = {}) {
      if (!this.pending && !this.unconfirmed && this.cart && Date.now() - this.confirmedAt < maxAge &&
          (checkoutReady(this.cart, this.config) || this.cart.items.length === 0)) return Promise.resolve(this.cart);
      return this.run(async () => {
        await this.reconcileCoverage();
        this.unconfirmed = false;
        return this.cart;
      }, {repair: false, forceRead: true, readonly: true});
    }
    addForm(formData, {onAdded} = {}) {
      scope.SpaceArmoryFitDevice?.decorateForm(formData);
      formData.delete('sections');
      formData.delete('sections_url');
      return this.run(async () => {
        const response = await this.post('cart/add.js', formData);
        try { onAdded?.(); } catch (error) { console.error(error); }
        return response;
      });
    }
    add(id, quantity = 1, properties = {}, {protection = false} = {}) {
      properties = {...properties, ...scope.SpaceArmoryFitDevice?.cartProperties()};
      return this.run(() => this.post('cart/add.js', {items: [{id, quantity, properties}]}), {protection});
    }
    setQuantity(key, quantity) {
      if (!Number.isInteger(quantity) || quantity < 0) return Promise.reject(new Error(coreText('cart.error_quantity', 'Enter a valid quantity.')));
      return this.run(async () => {
        if (!this.cart.items.some(item => item.key === key)) throw new Error(coreText('cart.item_changed', 'This item changed. Please review your cart.'));
        return this.change(key, quantity);
      });
    }
    swapVariant(key, id, propertyPatch) {
      return this.run(async () => {
        const original = this.cart.items.find(item => item.key === key);
        if (!original) throw new Error(coreText('cart.item_changed', 'This item changed. Please review your cart.'));
        const merged = {...original.properties, ...propertyPatch};
        Object.keys(merged).forEach(name => { if (merged[name] === null) delete merged[name]; });
        if (String(original.variant_id) === String(id)) return propertyPatch ? this.change(key, original.quantity, merged) : this.cart;
        const journal = {version: 2, originalKey: key, variant: String(original.variant_id), original: lineSignature(original)};
        const properties = {...merged, [SWAP]: JSON.stringify(journal)};
        const item = {id, quantity: original.quantity, properties};
        const sellingPlan = original.selling_plan_allocation?.selling_plan?.id;
        if (sellingPlan) item.selling_plan = sellingPlan;
        // An isolated new line makes an interrupted swap recoverable without touching an existing matching line.
        await this.post('cart/add.js', {items: [item]});
        await this.read();
        const replacement = this.cart.items.find(line => line.properties?.[SWAP] === properties[SWAP]);
        if (!replacement || replacement.quantity !== original.quantity) throw new Error(coreText('cart.error_quantity_size', 'That quantity is unavailable in this size. Your original item has been kept.'));
        const current = this.swapOriginal(journal);
        if (current) await this.change(current.key, 0);
        await this.recoverSwaps();
        return this.cart;
      }, {forceRead: true});
    }
    setProtection(selected) {
      return this.run(async () => {
        await this.reconcileCoverage(selected);
        this.unconfirmed = false;
        return this.cart;
      }, {repair: false, forceRead: true});
    }
    checkout(includeProtection) {
      return this.run(async () => {
        if (!merchandise(this.cart, this.config).length) throw new Error(coreText('cart.error_empty', 'Your cart is empty.'));
        await this.reconcileCoverage(includeProtection);
        this.unconfirmed = false;
        return this.cart;
      }, {repair: false, forceRead: true});
    }
  }
  scope.SpaceArmoryCartCore = {CartStore, protectionVariant, checkoutReady, merchandise, isCoverage, SWAP, COVER};
})(typeof window === 'undefined' ? globalThis : window);
